import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { formatSAR } from "@/i18n";
import { api } from "@/lib/api";
import { Loader2, CheckCircle2, Globe, Wallet } from "lucide-react";

export default function ReceiptPage() {
  const { reference } = useParams();
  const { t, lang } = useApp();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/remittance/receipt/${reference}`)
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, [reference]);

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-background p-6 text-center">
        <p className="text-muted-foreground" data-testid="receipt-not-found">{t.errorGeneric || "Receipt not found"}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <Loader2 className="w-7 h-7 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-950 to-background flex items-center justify-center p-5">
      <div data-testid="receipt-card" className="w-full max-w-sm bg-card rounded-3xl shadow-2xl overflow-hidden border border-border">
        <div className="bg-gradient-to-br from-slate-900 via-emerald-950 to-emerald-800 text-white p-6">
          <div className="flex items-center gap-2 mb-6">
            <Wallet className="w-5 h-5 text-emerald-300" />
            <span className="font-extrabold">Sb<span className="text-emerald-300">Pay</span></span>
          </div>
          <p className="text-xs text-white/60">{t.remittanceReceipt}</p>
          <p className="text-3xl font-black tabular-nums mt-1">{formatSAR(data.converted, lang)} {data.currency}</p>
          <p className="text-sm text-white/70 mt-1 flex items-center gap-1.5">
            <Globe className="w-4 h-4" /> {data.country_name} · {data.method}
          </p>
        </div>
        <div className="p-6 space-y-3.5 text-sm">
          <Row label={t.beneficiaryName} value={data.beneficiary_first_name} />
          <Row label={t.amountSent} value={`${formatSAR(data.amount_sar, lang)} ${t.currency}`} />
          <Row label={t.reference} value={data.reference} mono />
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{t.status}</span>
            <span data-testid="receipt-status" className={`flex items-center gap-1.5 font-bold ${data.delivered ? "text-emerald-600" : "text-amber-600"}`}>
              {data.delivered && <CheckCircle2 className="w-4 h-4" />}
              {t[`step_${data.current}`] || data.current}
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground pt-3 border-t border-border text-center">
            {t.poweredBy} <span className="font-bold text-foreground">SbPay</span> · {new Date(data.created_at).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US")}
          </p>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value, mono }) => (
  <div className="flex items-center justify-between">
    <span className="text-muted-foreground">{label}</span>
    <span className={`font-semibold ${mono ? "font-mono" : ""}`}>{value}</span>
  </div>
);
